/**
 * TaxPage
 * PIT-38 vergi hesabı — async queue + SSE ile.
 * POST /api/tax/jobs → per-exchange job IDs
 * SSE /api/imports/:id/stream → ilerleme takibi
 * GET /api/imports/:id/results → PIT-38 toplamları
 */
import { useState } from 'react';
import { Calculator, AlertTriangle, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import { useApiClient } from '../api/client';
import type { TaxJobsResponse, ImportJobSSEEvent, ImportResultsResponse } from '../api/client';

const CURRENT_YEAR = new Date().getFullYear();
const AVAILABLE_YEARS = Array.from({ length: CURRENT_YEAR - 2020 }, (_, i) => CURRENT_YEAR - 1 - i);

function formatPln(value: number): string {
  return value.toLocaleString('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

type JobStatus = 'queued' | 'processing' | 'done' | 'error';

interface JobProgress {
  jobId: string;
  exchangeSlug: string;
  exchangeName: string;
  status: JobStatus;
  rowCount?: number;
  errorMsg?: string;
  results?: ImportResultsResponse;
}

// Merge totals from multiple jobs into a single PIT-38 aggregate
function mergeTotals(jobs: JobProgress[]): ImportResultsResponse | null {
  const done = jobs.filter((j) => j.results);
  if (done.length === 0) return null;
  let pos22 = 0,
    pos23 = 0,
    pos36 = 0,
    pos37 = 0,
    rowCount = 0;
  for (const j of done) {
    pos22 += j.results!.pos22Revenue;
    pos23 += j.results!.pos23Cost;
    pos36 += j.results!.pos36Revenue;
    pos37 += j.results!.pos37Cost;
    rowCount += j.results!.rowCount;
  }
  const r2 = (n: number) => Math.round(n * 100) / 100;
  return {
    ...done[0].results!,
    pos22Revenue: r2(pos22),
    pos23Cost: r2(pos23),
    pos36Revenue: r2(pos36),
    pos37Cost: r2(pos37),
    pos38Income: r2(Math.max(0, pos36 - pos37)),
    pos39Loss: r2(Math.max(0, pos37 - pos36)),
    rowCount,
  };
}

const STATUS_LABEL: Record<JobStatus, string> = {
  queued: 'Sırada',
  processing: 'İşleniyor…',
  done: 'Tamamlandı',
  error: 'Hata',
};

export default function TaxPage() {
  const api = useApiClient();
  const [taxYear, setTaxYear] = useState<number>(CURRENT_YEAR - 1);
  const [phase, setPhase] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [jobs, setJobs] = useState<JobProgress[]>([]);
  const [globalError, setGlobalError] = useState<string | null>(null);

  function updateJob(jobId: string, patch: Partial<JobProgress>) {
    setJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, ...patch } : j)));
  }

  async function handleCalculate() {
    setPhase('running');
    setGlobalError(null);
    setJobs([]);

    let response: TaxJobsResponse;
    try {
      response = await api.startTaxJob(taxYear);
    } catch (err) {
      const msg = (err as { message?: string }).message ?? 'İş başlatılamadı.';
      setGlobalError(msg);
      setPhase('error');
      return;
    }

    if (response.jobs.length === 0) {
      setGlobalError('Bağlı borsa bulunamadı.');
      setPhase('error');
      return;
    }

    // Initialise job state
    const initial: JobProgress[] = response.jobs.map(({ jobId, exchangeSlug, exchangeName }) => ({
      jobId,
      exchangeSlug,
      exchangeName,
      status: 'queued',
    }));
    setJobs(initial);

    // Stream progress + fetch results for each job in parallel
    await Promise.allSettled(
      initial.map(async ({ jobId, exchangeName }) => {
        try {
          // SSE stream → receive status updates
          await api.streamImportJob(jobId, (event: ImportJobSSEEvent) => {
            updateJob(jobId, {
              status: event.status as JobStatus,
              rowCount: event.rowCount,
              errorMsg: event.errorMsg,
            });
          });

          // Fetch final results once done
          const results = await api.getResults(jobId);
          updateJob(jobId, { status: 'done', results });
        } catch (err) {
          const msg = (err as { message?: string }).message ?? `${exchangeName} hatası`;
          updateJob(jobId, { status: 'error', errorMsg: msg });
        }
      }),
    );

    setPhase('done');
  }

  const isRunning = phase === 'running';
  const totals = phase === 'done' ? mergeTotals(jobs) : null;
  const hasErrors = jobs.some((j) => j.status === 'error');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Calculator size={24} className="text-primary" />
        <h1 className="text-2xl font-bold">PIT-38 Vergi Hesabı</h1>
      </div>

      {/* Year selector & calculate button */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-base">Vergi Yılı Seçin</h2>
          <p className="text-sm text-base-content/60">
            Bağlı borsalarınızdaki tüm işlemler API üzerinden arka planda otomatik çekilir.
            İlerlemeyi gerçek zamanlı takip edebilirsiniz.
          </p>
          <div className="flex flex-wrap items-end gap-4 mt-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Yıl</span>
              </label>
              <select
                className="select select-bordered w-36"
                value={taxYear}
                onChange={(e) => setTaxYear(Number(e.target.value))}
                disabled={isRunning}
              >
                {AVAILABLE_YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => void handleCalculate()}
              disabled={isRunning}
            >
              {isRunning ? (
                <>
                  <span className="loading loading-spinner loading-sm" />
                  Hesaplanıyor…
                </>
              ) : (
                <>
                  {phase === 'done' ? <RefreshCw size={16} /> : <Calculator size={16} />}
                  {phase === 'done' ? 'Yeniden Hesapla' : 'Hesapla'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Global error */}
      {globalError && (
        <div className="alert alert-error">
          <AlertTriangle size={18} />
          <span>{globalError}</span>
        </div>
      )}

      {/* Per-exchange progress */}
      {jobs.length > 0 && (
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-base">İşlem Durumu</h2>
            <div className="space-y-3 mt-2">
              {jobs.map((job) => (
                <div
                  key={job.jobId}
                  className="flex items-center justify-between gap-4 p-3 rounded-lg bg-base-200"
                >
                  <div className="flex items-center gap-3">
                    {job.status === 'done' ? (
                      <CheckCircle size={18} className="text-success shrink-0" />
                    ) : job.status === 'error' ? (
                      <AlertTriangle size={18} className="text-error shrink-0" />
                    ) : (
                      <span className="loading loading-spinner loading-sm shrink-0" />
                    )}
                    <div>
                      <div className="font-medium">{job.exchangeName}</div>
                      {job.errorMsg && (
                        <div className="text-xs text-error mt-0.5">{job.errorMsg}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    {job.rowCount != null && (
                      <span className="text-base-content/60">{job.rowCount} işlem</span>
                    )}
                    <span
                      className={`badge badge-sm ${
                        job.status === 'done'
                          ? 'badge-success'
                          : job.status === 'error'
                            ? 'badge-error'
                            : job.status === 'processing'
                              ? 'badge-warning'
                              : 'badge-ghost'
                      }`}
                    >
                      {job.status === 'processing' && <Clock size={10} className="mr-1" />}
                      {STATUS_LABEL[job.status]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PIT-38 results (once all jobs complete) */}
      {totals && (
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="card-title text-base">PIT-38 Sonuçları — {taxYear}</h2>
              <div className="flex gap-2 items-center">
                {hasErrors && (
                  <span className="badge badge-warning badge-sm">Bazı borsalar hatalı</span>
                )}
                <span className="badge badge-neutral">{totals.rowCount} işlem</span>
              </div>
            </div>

            {/* Part C: Equities */}
            <div className="divider text-xs text-base-content/50">
              Bölüm C — Hisse Senetleri (Yabancı Broker)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="stat bg-base-200 rounded-box p-4">
                <div className="stat-title text-xs">Pos. 22 — Przychód (Gelir)</div>
                <div className="stat-value text-lg text-success">
                  {formatPln(totals.pos22Revenue)}
                </div>
              </div>
              <div className="stat bg-base-200 rounded-box p-4">
                <div className="stat-title text-xs">Pos. 23 — Koszty (Maliyet)</div>
                <div className="stat-value text-lg text-error">{formatPln(totals.pos23Cost)}</div>
              </div>
            </div>
            <div className="text-sm text-base-content/60 mt-1">
              Net (Pos. 22 − 23):{' '}
              <span
                className={
                  totals.pos22Revenue - totals.pos23Cost >= 0 ? 'text-success' : 'text-error'
                }
              >
                {formatPln(totals.pos22Revenue - totals.pos23Cost)}
              </span>
            </div>

            {/* Part E: Crypto */}
            <div className="divider text-xs text-base-content/50">
              Bölüm E — Sanal Para (Kripto)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="stat bg-base-200 rounded-box p-4">
                <div className="stat-title text-xs">Pos. 36 — Przychód (Gelir)</div>
                <div className="stat-value text-lg text-success">
                  {formatPln(totals.pos36Revenue)}
                </div>
              </div>
              <div className="stat bg-base-200 rounded-box p-4">
                <div className="stat-title text-xs">Pos. 37 — Koszty (Maliyet)</div>
                <div className="stat-value text-lg text-error">{formatPln(totals.pos37Cost)}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              <div className="stat bg-base-200 rounded-box p-4">
                <div className="stat-title text-xs">Pos. 38 — Dochód (Kazanç)</div>
                <div className="stat-value text-lg text-success">
                  {formatPln(totals.pos38Income)}
                </div>
              </div>
              <div className="stat bg-base-200 rounded-box p-4">
                <div className="stat-title text-xs">Pos. 39 — Strata (Zarar)</div>
                <div className="stat-value text-lg text-warning">{formatPln(totals.pos39Loss)}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
